import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../state/store';
import { SiteLogoProps } from './SiteLogo.types';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { CKSiteLogo } from './CKSiteLogo';
import { MinifiedCKSiteLogo } from './MinifiedCKSiteLogo';
import { IUSiteLogo } from './IUSiteLogo';
import { MinifiedIUSiteLogo } from './MinifiedIUSiteLogo';
import {
    FALLBACK_BRAND_LOGO_NAME,
    FALLBACK_MINIFIED_BRAND_LOGO_NAME,
} from '../../constants/clientConfigFallback';

export const SiteLogo: React.FC<SiteLogoProps> = ({ showTagLine = false, tagLine = "", varient }: SiteLogoProps) => {
    const theme = useTheme();
    const company = useSelector((state: RootState) => state.authentication.company);
    const clientConfig = useSelector((state: RootState) => state.clientConfig.data);

    const themeKey = theme.palette.mode === 'dark' ? 'dark' : 'light';
    const domainThemeSrc = clientConfig?.themeBrandLogos?.[themeKey];

    const componentRegistry = {
        CKSiteLogo,
        MinifiedCKSiteLogo,
        IUSiteLogo,
        MinifiedIUSiteLogo,
    } as const;

    const brandLogoName = (clientConfig?.brandLogo?.name as keyof typeof componentRegistry) || FALLBACK_BRAND_LOGO_NAME;
    const minifiedLogoName = (clientConfig?.minifiedBrandLogo?.name as keyof typeof componentRegistry) || FALLBACK_MINIFIED_BRAND_LOGO_NAME;

    const BrandComponent = componentRegistry[brandLogoName] || IUSiteLogo;
    const MinifiedComponent = componentRegistry[minifiedLogoName] || MinifiedIUSiteLogo;

    const companyLogoForTheme = theme.palette.mode === 'dark' ? company?.brand?.logo?.dark : company?.brand?.logo?.light;
    const hasCompanyBrandLogoForTheme = Boolean(companyLogoForTheme);

    const SelectedComponent = hasCompanyBrandLogoForTheme ? MinifiedComponent : BrandComponent;

    /** Dashboard / header: fixed 80px to align with company logo in Navbar. Login (domain image): max 140px. */
    const domainImgSx =
        varient === 'small'
            ? {
                height: 80,
                width: 'auto',
                maxWidth: 'min(320px, 50vw)',
                objectFit: 'contain' as const,
                display: 'block',
            }
            : {
                maxHeight: 140,
                width: 'auto',
                maxWidth: 'min(280px, 85vw)',
                objectFit: 'contain' as const,
                display: 'block',
            };

    if (domainThemeSrc) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: varient === 'small' ? '0.35rem 0' : '0',
                    /** Login (non-small): extra room before Sign In / Sign Up tabs; SVG logos keep default layout via `SelectedComponent`. */
                    marginBottom: varient === 'small' ? 0 : '20px',
                }}
            >
                <Box component="img" src={domainThemeSrc} alt="" sx={domainImgSx} />
            </Box>
        );
    }

    return (
        <SelectedComponent showTagLine={showTagLine} tagLine={tagLine} varient={varient} />
    );
};
